import React, { useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Button, Avatar, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import Table from '../../../commonComponents/Table';
import TablePagination from '../../../commonComponents/Pagination';
import { PhoneBlurred, OrderItemsContainer, OrderItemCard, OrderItemText, SectionTitle, RankingCard } from './styles';
import { CustomerWithOrders, Order, CustomersOrderTableProps } from './types';
import { formatDateAndTime } from '../../../utils/formatDateTime';
import { PHONE_BLUR_THRESHOLD, PHONE_UNBLUR_LENGTH, PHONE_SHORT_UNBLUR_LENGTH } from './constants';
import { useTheme } from '../../../context/ThemeContext';

const CustomersOrderTable: React.FC<CustomersOrderTableProps> = ({
  customers = [],
  currentPage = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  onPageSizeChange,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  const getPhoneBlurred = (phone: string): string => {
    if (phone.length > PHONE_BLUR_THRESHOLD) {
      return phone.slice(0, -PHONE_UNBLUR_LENGTH);
    } else {
      return phone.slice(0, -PHONE_SHORT_UNBLUR_LENGTH);
    }
  };

  const getPhoneUnBlurred = (phone: string): string => {
    if (phone.length > PHONE_BLUR_THRESHOLD) {
      return phone.slice(-PHONE_UNBLUR_LENGTH);
    } else {
      return phone.slice(-PHONE_SHORT_UNBLUR_LENGTH);
    }
  };

  const toggleCustomer = (customerId: string) => {
    if (expandedCustomerId === customerId) {
      setExpandedCustomerId(null);
      setExpandedOrderId(null);
    } else {
      setExpandedCustomerId(customerId);
      setExpandedOrderId(null);
    }
  };

  const toggleOrder = (orderId: string) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
    } else {
      setExpandedOrderId(orderId);
    }
  };

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize && onPageSizeChange) {
      onPageSizeChange(size);
    }
    if (page !== currentPage && onPageChange) {
      onPageChange(page);
    }
  };

  const columns: ColumnsType<CustomerWithOrders> = [
    {
      title: 'Customer Name',
      key: 'userName',
      render: (_, record) => (
        <Space>
          <Avatar size="small">{record.userName.charAt(0)}</Avatar>
          <span>{record.userName}</span>
        </Space>
      ),
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (_, record) => (
        <div style={{ display: 'flex' }}>
          <PhoneBlurred>{getPhoneBlurred(record.userPhone)}</PhoneBlurred>
          {getPhoneUnBlurred(record.userPhone)}
        </div>
      ),
    },
    {
      title: 'Total Spent',
      key: 'totalSpent',
      align: 'right',
      render: (_, record) => {
        const totalSpent = record.stats?.totalSpent ?? 0;
        return `$${!isNaN(totalSpent) ? parseFloat(String(totalSpent)).toFixed(2) : '0.00'}`;
      },
    },
    {
      title: 'Recent Visit',
      key: 'recentVisit',
      render: (_, record) => {
        const recentVisit = record.stats?.recentVisit;
        if (!recentVisit) return '-';
        const formatted = formatDateAndTime(recentVisit);
        return `${formatted.formattedDate} ${formatted.formattedTime}`;
      },
    },
    {
      title: 'Order Count',
      dataIndex: ['stats', 'orderCount'],
      key: 'orderCount',
      align: 'right',
    },
    {
      title: 'Average Spent',
      key: 'averageSpent',
      align: 'right',
      render: (_, record) => {
        const totalSpent = record.stats?.totalSpent ?? 0;
        const orderCount = record.stats?.orderCount ?? 1;
        const average = !isNaN(totalSpent) && !isNaN(orderCount)
          ? parseFloat(String(totalSpent)) / parseInt(String(orderCount))
          : 0;
        return `$${average.toFixed(2)}`;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const customerId = record.userPhone + record.userName;
        const isExpanded = expandedCustomerId === customerId;
        return (
          <Button
            type="link"
            onClick={() => toggleCustomer(customerId)}
            icon={<FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />}
          >
            {isExpanded ? 'Hide Details' : 'View Details'}
          </Button>
        );
      },
    },
  ];

  const expandedRowRender = (record: CustomerWithOrders) => {
    const customerId = record.userPhone + record.userName;
    const isExpanded = expandedCustomerId === customerId;

    if (!isExpanded) return null;

    const orderColumns: ColumnsType<Order> = [
      {
        title: 'Order Date & Time',
        key: 'date',
        render: (_, order) => {
          const formatted = formatDateAndTime(order.date);
          return `${formatted.formattedDate} ${formatted.formattedTime}`;
        },
      },
      {
        title: 'Total Amount',
        key: 'totalAmount',
        align: 'right',
        render: (_, order) => {
          const totalAmount = order.totalAmount ?? 0;
          return `$${!isNaN(totalAmount) ? parseFloat(String(totalAmount)).toFixed(2) : '0.00'}`;
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, order) => {
          const isOrderExpanded = expandedOrderId === order.id;
          return (
            <Button
              type="link"
              onClick={() => toggleOrder(order.id)}
              icon={<FontAwesomeIcon icon={isOrderExpanded ? faChevronUp : faChevronDown} />}
            >
              {isOrderExpanded ? 'Hide Details' : 'View Details'}
            </Button>
          );
        },
      },
    ];

    return (
      <div style={{ padding: '16px', backgroundColor: isDarkMode ? '#2D3748' : '#ffffff' }}>
        <SectionTitle level={5}>Orders</SectionTitle>
        <Table<Order>
          columns={orderColumns}
          dataSource={record.orders}
          rowKey="id"
          pagination={false}
          emptyText="No orders found"
          scroll={{ x: true }}
          headerBackgroundColor={isDarkMode ? '#2D3748' : '#F7FAFC'}
          removeVerticalBorders={true}
          thPadding="12px"
          tdPadding="12px"
          firstChildBorderRadius="8px"
          border="0"
          borderBottomLeftRight={true}
          theme={isDarkMode ? 'dark' : 'light'}
          expandable={{
            expandedRowRender: (order) => {
              if (expandedOrderId !== order.id) return null;
              return (
                <div style={{ padding: '16px' }}>
                  <SectionTitle level={5}>Order Items</SectionTitle>
                  <OrderItemsContainer>
                    {order.items?.map((item, index: number) => (
                      <OrderItemCard key={index}>
                        <OrderItemText>
                          <strong>Name:</strong> {item.name}
                        </OrderItemText>
                        <OrderItemText>
                          <strong>Quantity:</strong> {item.quantity}
                        </OrderItemText>
                        <OrderItemText>
                          <strong>Price:</strong> ${item.price}
                        </OrderItemText>
                      </OrderItemCard>
                    ))}
                  </OrderItemsContainer>
                </div>
              );
            },
            expandRowByClick: false,
          }}
        />
      </div>
    );
  };

  return (
    <RankingCard>
      <SectionTitle level={4}>Customer Orders</SectionTitle>
      <Table<CustomerWithOrders>
        columns={columns}
        dataSource={customers}
        rowKey={(record) => record.userPhone + record.userName}
        loading={loading}
        pagination={false}
        emptyText="No customers found"
        scroll={{ x: true }}
        headerBackgroundColor={isDarkMode ? '#2D3748' : '#F7FAFC'}
        removeVerticalBorders={true}
        thPadding="16px 12px"
        tdPadding="16px 12px"
        firstChildBorderRadius="8px"
        border="0"
        borderBottomLeftRight={true}
        theme={isDarkMode ? 'dark' : 'light'}
        expandable={{
          expandedRowRender,
          expandRowByClick: false,
        }}
      />
      {customers.length > 0 && onPageChange && onPageSizeChange && (
        <TablePagination
          totalCount={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total customers"
          padding="16px 0 0 0"
        />
      )}
    </RankingCard>
  );
};

export default CustomersOrderTable;
